import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { Context } from '../../../index';
import api from '../../../http';

// MUI icons
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'; // Заявки
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';             // Заказы на неделю
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';             // Заказы на доставке
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';                         // Архив заказов
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'; // Производство

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';                     // Клиенты
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';                       // Статус оплаты
import EarbudsOutlinedIcon from '@mui/icons-material/EarbudsOutlined';                           // Справочники
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';                     // Назад
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';                       // Настройки
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';                           // Выход
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';                   // Вернуться на сайт
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';   // Админ-панель
import ClientSwitcher from './ClientSwitcher';

const Sidebar = observer(() => {
  const location = useLocation();
  const { user } = useContext(Context);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // mobile toggle
  const [isMenuVisible, setIsMenuVisible] = useState(true);        // hide on scroll
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDirectoriesExpanded, setIsDirectoriesExpanded] = useState(
    JSON.parse(localStorage.getItem('isDirectoriesExpanded')) || false
  );
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(
    JSON.parse(localStorage.getItem('isOrdersExpanded')) || false
  );
  const [isContractsExpanded, setIsContractsExpanded] = useState(
    JSON.parse(localStorage.getItem('isContractsExpanded')) || false
  );
  const [activeItem, setActiveItem] = useState(location.pathname);
  const userRole = String(user?.role ?? ''); // 1=админ, 2=оператор, 3=клиент
  const navigate = useNavigate();

  // Основное меню (админ)
  const menuItems = [
    { name: 'Заявки',             path: '/Applications',     Icon: AssignmentTurnedInOutlinedIcon },
    { name: 'Заказы на неделю',   path: '/WeeklyOrders',     Icon: CalendarMonthOutlinedIcon },
    { name: 'Производство',       path: '/Production',       Icon: PrecisionManufacturingOutlinedIcon },
    { name: 'Заказы на доставке', path: '/OrdersOnDelivery', Icon: LocalShippingOutlinedIcon },
    { name: 'Путевые листы',      path: '/Waybill',          Icon: EarbudsOutlinedIcon },
    { name: 'Статус оплаты',      path: '/PaymentStatus',    Icon: PaymentsOutlinedIcon },
    { name: 'Архив заказов',      path: '/ArchiveOrders',    Icon: ArchiveOutlinedIcon },
    { name: 'Невыполненые заказы',      path: '/LostOrders',       Icon: ArchiveOutlinedIcon },
  ];

  // Нижний блок: «Настройки» ведёт на /settings (доступен и admin, и user).
  // Пункт «Админ-панель» виден только для роли админа (1).
  const bottomMenuItems = [
    ...(userRole === '1' ? [{
      name: 'Админ-панель', path: '/UserList', Icon: AdminPanelSettingsOutlinedIcon, isAdmin: true,
    }] : []),
    { name: 'Настройки',         path: '/settings', Icon: SettingsOutlinedIcon },
    { name: 'Назад',             path: null, Icon: ArrowBackOutlinedIcon,  isBack: true },
    { name: 'Выход',             path: null, Icon: LogoutOutlinedIcon,     isLogout: true },
    { name: 'Вернуться на сайт', path: '/',  Icon: StorefrontOutlinedIcon, isHome: true }
  ];

  // Подменю (если решишь вернуть разделы-дропдауны)
  const directoriesSubMenu = [
    { name: 'Водители',         path: '/driversComponent' },
    { name: 'Машины',           path: '/vehicles' },
    { name: 'Районы доставки',  path: '/DeliveryAreasComponent' },
    { name: 'Организации',      path: '/OrganizationDetails' },
    { name: 'Клиенты',          path: '/ClientsList' }
  ];

  const ordersSubMenu = [
    { name: 'Заявки',             path: '/Applications' },
    { name: 'Заказы на неделю',   path: '/WeeklyOrders' },
    { name: 'Заказы на доставке', path: '/OrdersOnDelivery' },
    { name: 'Архив заказов',      path: '/ArchiveOrders' }
  ];

  const contractsSubMenu = [
    { name: 'Договор 1', path: '/MyContracts', id: 1 },
    { name: 'Договор 2', path: '/MyContracts', id: 2 }
  ];

  // Меню клиента
  const clientMenuItems = [
    { name: 'Подать заявку', path: '/Apply',    Icon: AssignmentTurnedInOutlinedIcon },
    { name: 'Мои заказы',    path: '/MyOrders', Icon: AssignmentTurnedInOutlinedIcon },
  ];

  useEffect(() => {
    localStorage.setItem('isDirectoriesExpanded', JSON.stringify(isDirectoriesExpanded));
    localStorage.setItem('isOrdersExpanded', JSON.stringify(isOrdersExpanded));
    localStorage.setItem('isContractsExpanded', JSON.stringify(isContractsExpanded));
    localStorage.setItem('activeItem', activeItem);

    const handleScroll = () => {
      if (window.scrollY > lastScrollY) setIsMenuVisible(false);
      else setIsMenuVisible(true);
      setLastScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isDirectoriesExpanded, isOrdersExpanded, isContractsExpanded, activeItem]);

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location]);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleMenuItemClick = (item) => {
    if (item.isLogout) {
      handleLogout();
    } else if (item.isHome) {
      setActiveItem(item.path);
      navigate(item.path);
    } else if (item.isBack) {
      navigate(-1);
    } else if (item.name === 'Справочники') {
      setIsDirectoriesExpanded(!isDirectoriesExpanded);
    } else if (item.name === 'Заказы') {
      setIsOrdersExpanded(!isOrdersExpanded);
    } else if (item.name === 'Мои договора') {
      setIsContractsExpanded(!isContractsExpanded);
    } else {
      setActiveItem(item.path);
      navigate(item.path);
    }
    setIsSidebarVisible(false);
  };

  const handleLogout = async () => {
    // Гасим refresh-cookie на бэке (без блокировки UI на сетевую ошибку),
    // потом чистим MobX-store и localStorage, и уходим на главную сайта.
    try { await api.post('v1/logout', {}); } catch { /* no-op */ }
    user.logout();
    navigate('/');
  };

  const handleSubMenuItemClick = (path, _contractName, event) => {
    event.stopPropagation();
    setActiveItem(path);
    navigate(`${path}`);
    setIsSidebarVisible(false);
  };

  const renderMenuItem = (item) => {
    const isActive = item.path === activeItem;
    const IconCmp = item.Icon;

    return (
      <li
        key={item.name}
        className={`list__item ${isActive ? 'list__item--active' : ''}`}
        onClick={() => handleMenuItemClick(item)}
      >
        <div className="menu__item-content">
          <div className="menu__icon-container">
            {IconCmp && (
              <IconCmp
                className={`menu__icon ${isActive ? 'menu__icon--active' : ''}`}
                sx={{ fontSize: 22 }}
              />
            )}
          </div>
          <div className="menu__text-container">
            <span className="menu__text">{item.name}</span>
          </div>
        </div>

        {item.name === 'Справочники' && (
          <ul className={`submenu ${isDirectoriesExpanded ? 'submenu--visible' : ''}`}>
            {directoriesSubMenu.map((sub) => (
              <li
                key={sub.name}
                className={`submenu__item ${sub.path === activeItem ? 'submenu__item--active' : ''}`}
                onClick={(e) => handleSubMenuItemClick(sub.path, sub.name, e)}
              >
                {sub.name}
              </li>
            ))}
          </ul>
        )}

        {item.name === 'Заказы' && (
          <ul className={`submenu ${isOrdersExpanded ? 'submenu--visible' : ''}`}>
            {ordersSubMenu.map((sub) => (
              <li
                key={sub.name}
                className={`submenu__item ${sub.path === activeItem ? 'submenu__item--active' : ''}`}
                onClick={(e) => handleSubMenuItemClick(sub.path, sub.name, e)}
              >
                {sub.name}
              </li>
            ))}
          </ul>
        )}

        {item.name === 'Мои договора' && (
          <ul className={`submenu ${isContractsExpanded ? 'submenu--visible' : ''}`}>
            {contractsSubMenu.map((sub) => (
              <li
                key={sub.name}
                className={`submenu__item ${sub.path === activeItem ? 'submenu__item--active' : ''}`}
                onClick={(e) => handleSubMenuItemClick(sub.path + '/' + sub.id, '', e)}
              >
                {sub.name}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {isMenuVisible && (
        <button className="toggle-sidebar-btn" onClick={toggleSidebar}>
          ☰ Меню
        </button>
      )}

      <aside className={`sidebar ${isSidebarVisible ? 'sidebar-visible' : ''}`}>
        <div className="sidebar__inner">
          <div className="profile">
            <img
              src="https://i.pinimg.com/736x/ff/82/b6/ff82b607537ed90b2f343c643960acfa.jpg"
              alt="Profile"
              className="profile__pic"
            />
            <div className="profile__info">
              <span className="profile__name">
                {((user?.profile?.secondname || '') + ' ' + (user?.profile?.firstname || '')).trim()
                  || user?.user?.login || '—'}
              </span>
            </div>
          </div>

          <ClientSwitcher />

          <nav className="sidebar__menu">
            <ul className="menu__list">
              {userRole === '1' && menuItems.map(renderMenuItem)}
              {userRole === '3' && clientMenuItems.map(renderMenuItem)}
            </ul>

            <ul className="menu__list menu__list--bottom">
              {bottomMenuItems.map(renderMenuItem)}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
});

export default Sidebar;

